import { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import httpClient from '../services/httpClient';
import SessionExpiredModal from '../components/shared/SessionExpiredModal';

const AuthContext = createContext({});

/** Rotas públicas em que o aviso de sessão expirada não deve ser exibido */
const PUBLIC_PATHS = ['/', '/ativar-conta', '/recuperar-senha', '/redefinir-senha'];

function isPublicPath(pathname) {
    const p = pathname?.replace(/\/$/, '') || '';
    return PUBLIC_PATHS.some((path) => p === path || p.startsWith(path + '/'));
}

export function AuthProvider({ children }) {
    const location = useLocation();
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSessionExpired, setIsSessionExpired] = useState(false);

    const showSessionExpiredModal = isSessionExpired && !isPublicPath(location.pathname);

    useEffect(() => {
        async function validateToken() {
            const storedUser = localStorage.getItem('user');
            const storedToken = localStorage.getItem('token');

            if (storedUser && storedToken) {
                httpClient.setToken(storedToken);

                try {
                    // Tenta validar o token com o backend
                    const response = await httpClient.get('/usuarios/validate-token');

                    if (response.ok) {
                        const userData = JSON.parse(storedUser);
                        setUser(userData);
                        if (userData?.personId != null) {
                            localStorage.setItem('personId', String(userData.personId));
                        }
                    } else {
                        // Token inválido, limpa tudo
                        localStorage.removeItem('token');
                        localStorage.removeItem('refreshToken');
                        localStorage.removeItem('user');
                        localStorage.removeItem('personId');
                        httpClient.setToken(null);
                        setUser(null);
                    }
                } catch (error) {
                    // Em caso de erro na validação, limpa tudo por segurança
                    localStorage.removeItem('token');
                    localStorage.removeItem('refreshToken');
                    localStorage.removeItem('user');
                    localStorage.removeItem('personId');
                    httpClient.setToken(null);
                    setUser(null);
                }
            }

            setIsLoading(false);
        }

        validateToken();
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('personId');
        httpClient.setToken(null);
        setUser(null);
        setIsSessionExpired(false);
    }, []);

    useEffect(() => {
        // Registra o callback de logout no httpClient
        const handleUnauthorized = () => {
            logout();
        };
        
        // Registra o callback de sessão expirada
        const handleSessionExpired = () => {
            setIsSessionExpired(true);
        };
        
        httpClient.setOnUnauthorized(handleUnauthorized);
        httpClient.setOnSessionExpired(handleSessionExpired);
        
        // Cleanup: remove callbacks quando componente desmonta
        return () => {
            httpClient.setOnUnauthorized(null);
            httpClient.setOnSessionExpired(null);
        };
    }, [logout]);

    async function login(email, password) {
        try {
            const response = await httpClient.post('/usuarios/login', { email, password });

            if (response.ok) {
                const data = await response.json();

                // Assuming the backend returns { token, ...userProfile } or similar.
                // We'll store the whole response data as user for now, or extract what we need.
                // If the backend only returns token, we would need to decode it or fetch user details.
                // For this implementation, I'll assume we get the necessary info.

                const { token, userData, refreshToken } = data;

                // If userData is empty (only token returned), we might need to fetch /me
                // But let's assume for now we get what we need or the token has it.
                // We'll store the token separately.

                localStorage.setItem('token', token);
                localStorage.setItem('refreshToken', refreshToken);
                localStorage.setItem('user', JSON.stringify(userData));
                if (userData?.personId != null) {
                    localStorage.setItem('personId', String(userData.personId));
                }

                httpClient.setToken(token);
                setUser(userData);
                return { success: true };
            } else {
                return { success: false, message: 'Credenciais inválidas' };
            }
        } catch (error) {
            return { success: false, message: 'Erro ao conectar com o servidor' };
        }
    }

    const handleSessionExpiredConfirm = useCallback(() => {
        logout();
    }, [logout]);

    return (
        <AuthContext.Provider value={{ user, signed: !!user, login, logout, isLoading }}>
            {children}
            {showSessionExpiredModal && <SessionExpiredModal onConfirm={handleSessionExpiredConfirm} />}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
